import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function MyPlanTemplateChart({ projectData }) {
  const gantt = window.gantt;
  const navigate = useNavigate();
  const ganttContainer = useRef(null);
  const [showGantt, setShowGantt] = useState(false); // Default to task list view
  const [ganttInstance, setGanttInstance] = useState(null);
  useEffect(() => {
    // Initialize gantt instance after component mount
    if (window.gantt && !ganttInstance) {
      setGanttInstance(window.gantt);
    }
  }, []);
  useEffect(() => {
    if (ganttContainer.current && showGantt && ganttInstance) {
      ganttInstance.clearAll();

      // Set read-only configurations
      ganttInstance.config.readonly = true;
      ganttInstance.config.drag_move = false;
      ganttInstance.config.drag_progress = false;
      ganttInstance.config.drag_resize = false;
      ganttInstance.config.drag_links = false;
      ganttInstance.config.click_drag = false;

      // Adjust layout settings
      ganttInstance.config.fit_tasks = true;
      ganttInstance.config.min_column_width = 40;
      ganttInstance.config.row_height = 35;
      ganttInstance.config.task_height = 16;
      ganttInstance.config.scale_height = 50;
      ganttInstance.config.smart_scales = true;

      // Hide grid and only show chart
      ganttInstance.config.show_grid = false;
      ganttInstance.config.show_chart = true;

      // Show all tasks expanded by default
      ganttInstance.config.open_tree_initially = true;

      // Enable links display
      ganttInstance.config.show_links = true;
      ganttInstance.config.links_arrows = true;

      // Fixed dimensions for the chart
      ganttInstance.config.autosize = "none";
      ganttInstance.config.height = 350;

      // Initialize the gantt chart
      ganttInstance.init(ganttContainer.current);

      // Prepare the tasks and links data
      if (projectData && projectData.length > 0) {
        // Process tasks
        const tasks = projectData.map((task) => ({
          id: task.id + 1,
          text: task.text || task.name || "Untitled Task",
          start_date: task.start_date ? formatDate(task.start_date) : new Date(),
          duration: cleanDuration(task.duration) || 1,
          parent:
            typeof task.parent === "string" && task.parent.includes("id=")
              ? extractTaskIdFromParent(task.parent)
              : task.parent || 0,
          progress: task.progress || 0,
          open: true
        }));

        // Generate links between consecutive tasks
        const links = [];
        for (let i = 0; i < tasks.length - 1; i++) {
          links.push({
            id: i + 1,
            source: tasks[i].id,
            target: tasks[i + 1].id,
            type: "0" // Finish to Start link type
          });
        }

        // Parse the Gantt chart data with tasks and links
        ganttInstance.parse({
          data: tasks,
          links: links
        });

        // Ensure all tasks are expanded after parsing
        ganttInstance.eachTask(task => {
          ganttInstance.open(task.id);
        });
      }
    }

    // Cleanup on component unmount or dependency change
    return () => {
      if (ganttInstance) {
        ganttInstance.clearAll();
        // Force refresh of the chart container
        if (ganttContainer.current) {
          ganttContainer.current.innerHTML = '';
        }
      }
    };
  }, [projectData, showGantt, ganttInstance]);
  // Utility Functions
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const extractTaskIdFromParent = (parentString) => {
    const match = parentString.match(/id=(\d+)/);
    return match ? parseInt(match[1], 10) + 1 : "";
  };

  const cleanDuration = (duration) => {
    if (!duration) return 0.0; // Handle undefined/null durations
    const numericValue = parseFloat(duration.toString().replace("d", "").trim());
    return isNaN(numericValue) ? 0.0 : parseFloat(numericValue.toFixed(1));
  };


  return (
    <div style={{ width: "100%", height: "400px", position: "static" }}>
      {/* Toggle Button */}
      <button
        onClick={() => setShowGantt((prev) => !prev)}
        style={{
          position: "absolute",
          top: "90px",
          right: "60px",
          zIndex: 1000,
          padding: "8px 12px",
          cursor: "pointer",
          backgroundColor: "#fff",
          color: "black",
          border: "1px solid gray",
          borderRadius: "5px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          fontSize: "12px"
        }}
      >
        {showGantt ? "Task List" : "Gantt Chart"}
      </button>

      {/* Task List View */}
      {!showGantt && (
        <div>
          {/* Task Details */}
          <div
            style={{
              width: "95%", // Reduced width
              height: "350px",
              overflowY: "auto",
              borderRadius: "10px",
              boxShadow: "0 4px 8px rgba(148, 132, 132, 0.2)",
              padding: "10px",
              marginBottom: "20px",
              backgroundColor: "#fff",
            }}
          >
            <table style={{ width: "100%", fontSize: "14px", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd", textAlign: "left", width: "60%" }}>
                    Task Name
                  </th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd", textAlign: "center", width: "20%" }}>
                    Start Date
                  </th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd", textAlign: "center", width: "20%" }}>
                    Duration
                  </th>
                </tr>
              </thead>
              <tbody>
                {projectData && projectData.length ? (
                  projectData.map((task) => (
                    <tr key={task.id}>
                      <td
                        style={{
                          fontSize: "12px",
                          padding: "10px",
                          borderBottom: "1px solid #ddd",
                          textAlign: "left"
                        }}
                      >
                        {task.text || task.name || "Untitled Task"}
                      </td>
                      <td
                        style={{
                          fontSize: "12px",
                          padding: "10px",
                          borderBottom: "1px solid #ddd",
                          textAlign: "center"
                        }}
                      >
                        {task.start_date ? formatDate(task.start_date) : "N/A"}
                      </td>
                      <td
                        style={{
                          fontSize: "12px",
                          padding: "10px",
                          borderBottom: "1px solid #ddd",
                          textAlign: "center"
                        }}
                      >
                        {cleanDuration(task.duration)} days
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      style={{
                        padding: "10px",
                        textAlign: "center",
                      }}
                    >
                      No tasks available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Gantt Chart View */}
      {showGantt && (
        <div
          ref={ganttContainer}
          style={{
            width: "100%",
            minWidth: "550px",
            height: "350px",
            position: "relative",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(148, 132, 132, 0.2)",
            overflow: "visible"
          }}
        ></div>
      )}
    </div>
  );
}